/**
 * English: Suggest flows with AI
 *
 * What this file covers: Suggest flows with AI: the page where a business gives its ideas, the AI suggests flows from its knowledge base, its Insights report and its flows, asks questions in a chat, and builds only the picked ones (switched off); its settings, models and credits, the AI's live activity and memory.
 *
 * Tone: Clear and encouraging, for business owners planning their chatbot. Say what the AI is doing in plain words; be exact about credits and that nothing is built or switched on without the person.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const flowIdeas: Catalog = {
  'flowIdeas.title': 'Suggest flows with AI',
  'flowIdeas.description': 'Tell the AI what you have in mind, or nothing at all. It suggests flows from your knowledge base, what your customers asked and the flows you already have, asks when something is unclear, and builds only the ones you pick.',
  'flowIdeas.backToFlows': 'Back to Flows',
  'flowIdeas.tabs.flows': 'Your flows',
  'flowIdeas.tabs.ai': 'AI',
  'flowIdeas.tabs.attention': 'The AI is working or has asked you something',
  'flowIdeas.start.title': 'What should your chatbot do?',
  'flowIdeas.start.intro': 'Start here. The AI works on the right and shows what it is doing; its suggestions come back here for you to pick from.',
  'flowIdeas.start.ideas': 'Your ideas (optional)',
  'flowIdeas.start.ideasHint': 'Type or paste anything: a list, an email, how you handle enquiries now. Leave it empty and the AI suggests flows from your knowledge base and what your customers asked.',
  'flowIdeas.start.ideasPlaceholder': 'For example: parents ask about fees for each grade, school transport and open days. We want to book school tours and take applications.',
  'flowIdeas.start.settings': 'Settings',
  'flowIdeas.start.change': 'Change settings',
  'flowIdeas.start.suggest': 'Suggest flows',
  'flowIdeas.start.suggestFromKb': 'Suggest from my knowledge base',
  'flowIdeas.start.price': 'About {{credits}} for the suggestions. Takes about a minute.',
  'flowIdeas.start.priceUnknown': 'The price is worked out when it starts. Takes about a minute.',
  'flowIdeas.summary.knowledge': 'Knowledge base',
  'flowIdeas.summary.documents': 'Chosen documents: {{count}}',
  'flowIdeas.summary.insights': 'Insights of {{date}}',
  'flowIdeas.summary.count': '{{count}} suggestions',
  'flowIdeas.summary.safetyOn': 'Safety on',
  'flowIdeas.summary.safetySome': 'Safety partly on',
  'flowIdeas.summary.safetyOff': 'Safety off',
  'flowIdeas.settings.title': 'Settings',
  'flowIdeas.settings.uses.title': 'What the AI uses',
  'flowIdeas.settings.uses.knowledge': 'Your knowledge base',
  'flowIdeas.settings.uses.knowledgeHint': 'Your documents and corrections ({{count}} documents), so the flows answer from what you have written.',
  'flowIdeas.settings.uses.allDocuments': 'Every document',
  'flowIdeas.settings.uses.allDocumentsHint': 'Switch off to choose the documents the AI reads.',
  'flowIdeas.settings.uses.documents': 'Documents to use',
  'flowIdeas.settings.uses.insights': 'What customers asked',
  'flowIdeas.settings.uses.insightsHint': 'Your Insights report of {{date}}: the questions nobody answered, the top topics and where chats stopped.',
  'flowIdeas.settings.uses.flows': 'Your existing flows ({{count}})',
  'flowIdeas.settings.uses.flowsHint': 'So it never repeats one, suggests improvements to them and links new flows to them.',
  'flowIdeas.settings.uses.apis': 'Your APIs and live sources ({{count}})',
  'flowIdeas.settings.uses.apisHint': 'So a flow can look things up or send details to your systems.',
  'flowIdeas.settings.uses.bookings': 'Bookings',
  'flowIdeas.settings.uses.bookingsHint': 'Flows may book your services and classes.',
  'flowIdeas.settings.uses.tickets': 'Tickets',
  'flowIdeas.settings.uses.ticketsHint': 'Flows may open tickets for your team.',
  'flowIdeas.settings.goal.title': 'Goal and detail',
  'flowIdeas.settings.goal.goals': 'What the flows are for',
  'flowIdeas.settings.goal.goalsHint': 'Pick any number, or none to let the AI decide from your business.',
  'flowIdeas.settings.goal.count': 'How many suggestions',
  'flowIdeas.settings.goal.countHint': '{{min}} to {{max}}. You can ask for more later in the chat.',
  'flowIdeas.settings.goal.detail': 'Detail',
  'flowIdeas.settings.voice.title': 'Channels, language and tone',
  'flowIdeas.settings.voice.channels': 'Channels',
  'flowIdeas.settings.voice.channelsHint': 'Where the flows will run. Each channel has its own limits on buttons and lists.',
  'flowIdeas.settings.voice.language': 'Language the flows speak',
  'flowIdeas.settings.voice.languageHint': 'The language customers get the messages in. Taken from your chatbot.',
  'flowIdeas.settings.voice.tone': 'Tone',
  'flowIdeas.settings.voice.toneHint': 'Taken from your chatbot\'s persona.',
  'flowIdeas.settings.voice.bot': 'Chatbot',
  'flowIdeas.settings.voice.botHint': 'The chatbot the new flows belong to.',
  'flowIdeas.settings.voice.botDefault': '{{name}} (main)',
  'flowIdeas.settings.safety.title': 'Safety and hand-over',
  'flowIdeas.settings.safety.person': 'Every flow offers a person',
  'flowIdeas.settings.safety.personHint': 'A customer can always ask to talk to someone on your team.',
  'flowIdeas.settings.safety.hours': 'Respect office hours',
  'flowIdeas.settings.safety.hoursHint': 'Outside your hours, flows say when someone will reply instead of promising an answer now.',
  'flowIdeas.settings.safety.consent': 'Ask before collecting details',
  'flowIdeas.settings.safety.consentHint': 'Flows ask the customer\'s permission before they keep a name, number or email (POPIA).',
  'flowIdeas.settings.models.title': 'Models',
  'flowIdeas.settings.models.suggest': 'Model for suggestions',
  'flowIdeas.settings.models.suggestPrice': 'About {{credits}} a message to the AI.',
  'flowIdeas.settings.models.build': 'Model for building flows',
  'flowIdeas.settings.models.buildPrice': 'About {{credits}} a flow.',
  'flowIdeas.settings.models.note': 'A stronger model thinks more carefully and uses your credits faster. Suggestions are one message at a time, so a strong model costs little there.',
  'flowIdeas.settings.modelStandard': '{{name}} (standard)',
  'flowIdeas.settings.priceUnknown': 'The price is worked out when it is used.',
  'flowIdeas.settings.done': 'Done',
  'flowIdeas.settings.save': 'Save settings',
  'flowIdeas.settings.saved': 'Settings saved. The AI uses them from its next message.',
  'flowIdeas.goal.answer_questions': 'Answer questions',
  'flowIdeas.goal.capture_leads': 'Capture leads',
  'flowIdeas.goal.bookings': 'Bookings',
  'flowIdeas.goal.support': 'Support and complaints',
  'flowIdeas.goal.sales': 'Sales and orders',
  'flowIdeas.goal.enrolment': 'Enrolment and applications',
  'flowIdeas.goal.after_hours': 'After hours',
  'flowIdeas.goal.feedback': 'Feedback and surveys',
  'flowIdeas.detail.simple.name': 'Simple',
  'flowIdeas.detail.simple.hint': '3 to 6 steps: quick to check and easy to change.',
  'flowIdeas.detail.complete.name': 'Complete',
  'flowIdeas.detail.complete.hint': 'Forms, branches and confirmations, ready for real customers.',
  'flowIdeas.channel.whatsapp': 'WhatsApp',
  'flowIdeas.channel.web': 'Website chat',
  'flowIdeas.channel.messenger': 'Messenger',
  'flowIdeas.channel.instagram': 'Instagram',
  'flowIdeas.tone.friendly': 'Friendly',
  'flowIdeas.tone.formal': 'Formal',
  'flowIdeas.tone.playful': 'Playful',
  'flowIdeas.lang.en': 'English',
  'flowIdeas.lang.af': 'Afrikaans',
  'flowIdeas.lang.zu': 'isiZulu',
  'flowIdeas.lang.xh': 'isiXhosa',
  'flowIdeas.lang.st': 'Sesotho',
  'flowIdeas.lang.tn': 'Setswana',
  'flowIdeas.lang.nso': 'Sepedi',
  'flowIdeas.lang.pt': 'Portuguese',
  'flowIdeas.lang.fr': 'French',
  'flowIdeas.priority.high': 'High priority',
  'flowIdeas.priority.medium': 'Medium priority',
  'flowIdeas.priority.low': 'Low priority',
  'flowIdeas.need.document': 'Document',
  'flowIdeas.need.template': 'WhatsApp template',
  'flowIdeas.need.number': 'Number',
  'flowIdeas.need.api': 'API',
  'flowIdeas.need.info': 'Information',
  'flowIdeas.source.document': 'A document in your knowledge base',
  'flowIdeas.source.correction': 'A correction you wrote',
  'flowIdeas.source.flow': 'One of your flows',
  'flowIdeas.source.insight': 'Your Insights report',
  'flowIdeas.source.idea': 'Your ideas',
  'flowIdeas.source.api': 'One of your APIs',
  'flowIdeas.status.idle': 'Waiting for you',
  'flowIdeas.status.thinking': 'The AI is thinking',
  'flowIdeas.status.building': 'Building your flows',
  'flowIdeas.status.done': 'Done',
  'flowIdeas.status.failed': 'Something went wrong',
  'flowIdeas.status.cancelled': 'Stopped',
  'flowIdeas.itemStatus.proposed': 'Suggested',
  'flowIdeas.itemStatus.removed': 'Removed',
  'flowIdeas.itemStatus.queued': 'Waiting',
  'flowIdeas.itemStatus.building': 'Building',
  'flowIdeas.itemStatus.created': 'Created',
  'flowIdeas.itemStatus.failed': 'Not built',
  'flowIdeas.itemStatus.skipped': 'Not created',
  'flowIdeas.set.title': 'Your suggestions',
  'flowIdeas.set.cancel': 'Stop',
  'flowIdeas.set.new': 'Start again',
  'flowIdeas.set.waiting': 'The AI is reading and writing. Watch what it is doing on the right; its suggestions appear here.',
  'flowIdeas.set.none': 'No suggestions yet',
  'flowIdeas.set.noneHint': 'Answer the AI\'s questions or tell it more in the chat, and its suggestions appear here.',
  'flowIdeas.cards.title': 'Suggested flows: {{count}}',
  'flowIdeas.cards.hint': 'Tick the ones you want. Nothing is built until you press Build, and every flow starts switched off.',
  'flowIdeas.cards.goal': 'Goal',
  'flowIdeas.cards.anyGoal': 'Any goal',
  'flowIdeas.cards.priority': 'Priority',
  'flowIdeas.cards.anyPriority': 'Any priority',
  'flowIdeas.cards.filteredOut': 'No suggestion matches these filters.',
  'flowIdeas.card.number': 'Suggestion {{n}}',
  'flowIdeas.card.new': 'New flow',
  'flowIdeas.card.improve': 'Improvement',
  'flowIdeas.card.improves': 'Improves your flow',
  'flowIdeas.card.pick': 'Build {{name}}',
  'flowIdeas.card.purpose': 'Purpose',
  'flowIdeas.card.outcome': 'Outcome',
  'flowIdeas.card.starts': 'When it starts',
  'flowIdeas.card.steps': 'Steps',
  'flowIdeas.card.stepsNow': 'Steps now',
  'flowIdeas.card.stepsAfter': 'Steps after',
  'flowIdeas.card.why': 'Why',
  'flowIdeas.card.sources': 'What this is based on',
  'flowIdeas.card.uses': 'Uses',
  'flowIdeas.card.usesFlow': 'Runs your flow {{name}}',
  'flowIdeas.card.usesBookings': 'Bookings',
  'flowIdeas.card.usesTickets': 'Tickets',
  'flowIdeas.card.needs': 'Needs from you',
  'flowIdeas.card.check': 'Check before building',
  'flowIdeas.card.price': 'About {{credits}} to build',
  'flowIdeas.card.priceUnknown': 'Price worked out when built',
  'flowIdeas.card.ask': 'Ask the AI to change this',
  'flowIdeas.card.askPrefill': 'About “{{name}}”: ',
  'flowIdeas.starts.keywords': 'When a customer writes {{keywords}}',
  'flowIdeas.starts.intent': 'When a customer wants to {{intent}}',
  'flowIdeas.starts.entry': 'As your {{entry}} flow',
  'flowIdeas.starts.unknown': 'Not set yet',
  'flowIdeas.flag.notGrounded': 'Not grounded: nothing in your knowledge base, flows or ideas backs this. Check it before building.',
  'flowIdeas.flag.factNotFound': '“{{detail}}” is not in your documents. The flow should answer it from the knowledge base instead.',
  'flowIdeas.flag.unknownDocument': 'The document “{{detail}}” was not found in your knowledge base.',
  'flowIdeas.flag.unknownApi': 'The API “{{detail}}” was not found.',
  'flowIdeas.flag.unknownFlow': 'The flow “{{detail}}” was not found.',
  'flowIdeas.flag.keywordClash': 'The keyword “{{detail}}” is already used by {{flow}}.',
  'flowIdeas.flag.keywordClashAny': 'The keyword “{{detail}}” is already used by another flow.',
  'flowIdeas.flag.overlaps': 'Close to your flow {{flow}}. Improving that flow may be better.',
  'flowIdeas.flag.overlapsAny': 'Close to one of your flows.',
  'flowIdeas.flag.entryTaken': '{{flow}} already handles this.',
  'flowIdeas.flag.entryTakenAny': 'Another flow already handles this.',
  'flowIdeas.build.picked': 'Picked: {{count}}',
  'flowIdeas.build.pickSome': 'Tick the suggestions you want built.',
  'flowIdeas.build.total': 'About {{credits}} to build them',
  'flowIdeas.build.totalUnknown': 'The price is worked out as they are built.',
  'flowIdeas.build.room': 'Your plan allows {{count}} more flows.',
  'flowIdeas.build.roomUnlimited': 'Your plan has no limit on flows.',
  'flowIdeas.build.model': 'Model for building',
  'flowIdeas.build.button': 'Build {{count}}',
  'flowIdeas.build.switchedOff': 'Created switched off: nothing reaches customers until you switch a flow on.',
  'flowIdeas.build.started': 'Building has started. You can leave this page: we will email you and send a notification when they are ready.',
  'flowIdeas.confirm.title': 'Build these flows ({{count}})?',
  'flowIdeas.confirm.model': 'Built with {{model}}.',
  'flowIdeas.confirm.price': 'About {{credits}} in total.',
  'flowIdeas.confirm.off': 'Every flow is created switched off. Open each in the builder, test it, then switch it on.',
  'flowIdeas.confirm.improve': 'An improvement is built as a copy of your flow. Your flow stays as it is until you replace it with the copy.',
  'flowIdeas.confirm.room': 'Your plan has room for {{fit}} of these {{count}}. The rest will not be created.',
  'flowIdeas.confirm.leave': 'Building takes about a minute a flow. You can leave this page: you get an email and a notification when they are ready.',
  'flowIdeas.results.title': 'Your new flows',
  'flowIdeas.results.building': 'Building your flows',
  'flowIdeas.results.count': '{{done}} of {{total}} done, {{created}} created',
  'flowIdeas.results.leave': 'You can leave this page. You get an email and a notification when they are ready.',
  'flowIdeas.results.off': 'Switched off',
  'flowIdeas.results.open': 'Open in builder',
  'flowIdeas.results.problems': 'Check in the builder',
  'flowIdeas.results.needs': 'Still needs',
  'flowIdeas.results.clashes': 'Keyword clashes',
  'flowIdeas.results.switch': 'Switch to the improved one',
  'flowIdeas.results.switchTitle': 'Switch to the improved flow?',
  'flowIdeas.results.switchBody': '{{name}} is switched on and {{original}} is switched off. Both are kept, so you can switch back at any time.',
  'flowIdeas.results.switched': 'Now in use',
  'flowIdeas.results.switchedToast': 'The improved flow is now switched on. The original is kept, switched off.',
  'flowIdeas.ai.title': 'AI',
  'flowIdeas.ai.idle': 'When you start, I show here what I am doing: reading your knowledge base, checking your flows and writing suggestions. If something is unclear I ask you here, and you can tell me to change anything.',
  'flowIdeas.ai.thinking': 'Thinking...',
  'flowIdeas.ai.placeholder': 'Ask for changes, more suggestions, or to build some',
  'flowIdeas.ai.placeholderAnswer': 'Answer, or tap one of the buttons above',
  'flowIdeas.ai.send': 'Send',
  'flowIdeas.ai.wait': 'Wait for the AI to finish.',
  'flowIdeas.ai.estimate': 'This message: about {{in}} tokens in, {{out}} out, about {{credits}}.',
  'flowIdeas.ai.estimateNone': 'Type a message to see what it costs.',
  'flowIdeas.ai.wontFit': 'This would be too much for the AI to keep track of. Start a fresh set to carry on.',
  'flowIdeas.ai.used': 'Used {{tokens}} tokens, {{credits}}',
  'flowIdeas.ai.spent': 'This set: {{credits}}, {{tokens}} tokens',
  'flowIdeas.activity.title': 'What the AI is doing',
  'flowIdeas.activity.knowledge': 'Reading your knowledge base ({{count}} documents)',
  'flowIdeas.activity.insights': 'Looking at what customers asked (Insights of {{date}})',
  'flowIdeas.activity.flows': 'Checking your {{count}} flows',
  'flowIdeas.activity.apis': 'Looking at your APIs ({{count}})',
  'flowIdeas.activity.writing': 'Writing suggestions',
  'flowIdeas.activity.writingRange': 'Writing suggestions {{from}} to {{to}} of {{total}}',
  'flowIdeas.activity.folding': 'Summarising our earlier conversation',
  'flowIdeas.activity.building': 'Building “{{name}}” ({{n}} of {{total}})',
  'flowIdeas.activity.checking': 'Checking the flow',
  'flowIdeas.activity.checkingNamed': 'Checking “{{name}}”',
  'flowIdeas.memory.label': 'Memory',
  'flowIdeas.memory.used': '{{used}} of {{budget}} tokens',
  'flowIdeas.memory.green': 'Plenty of room: the AI keeps track of everything.',
  'flowIdeas.memory.amber': 'Filling up: older chat will be summarised to keep the AI accurate.',
  'flowIdeas.memory.red': 'Nearly full: the AI may soon need a fresh set to stay accurate.',
  'flowIdeas.memory.folded': 'I summarised our earlier conversation to stay accurate.',
  'flowIdeas.memory.showNote': 'Show the summary',
  'flowIdeas.memory.hideNote': 'Hide the summary',
  'flowIdeas.memory.tooBig': 'This conversation has grown too big for the AI to stay accurate. Start a fresh set: your settings, the suggestions you kept and a summary of this chat come with you.',
  'flowIdeas.memory.fresh': 'Start a fresh set',
  'flowIdeas.history.title': 'Earlier suggestions',
  'flowIdeas.history.fromKb': 'From your knowledge base',
  'flowIdeas.history.counts': '{{suggestions}} suggested, {{created}} built',
  'flowIdeas.fromInsights.intro': 'From my Insights report, customers asked about these and did not get an answer:',
  'flowIdeas.fromInsights.gap': '- {{theme}} ({{count}} chats): {{why}}',
  'flowIdeas.fromInsights.document': '- A document to add: {{title}}',
  'flowIdeas.error.credits': 'There is not enough credit for this. Top up under Billing, or choose the standard model.',
  'flowIdeas.error.busy': 'The AI is still busy with this set. Wait for it to finish, or stop it.',
  'flowIdeas.error.tooBig': 'This set has grown too big for the AI to stay accurate. Start a fresh set to carry on.',
  'flowIdeas.error.planLimit': 'Your plan allows no more flows. Entry points do not count. Change your plan under Billing, or remove flows you do not use.',
  'flowIdeas.error.model': 'That model is not available now. Choose another in the settings.',
  'flowIdeas.error.forbidden': 'Only the owner or an admin can suggest and build flows.',
  'flowIdeas.chat.showAll': 'Show all ({{count}} characters)',
  'flowIdeas.chat.showLess': 'Show less',
  'flowIdeas.activity.looseEnds': 'Checking your flows for loose ends ({{count}} found)',
  'flowIdeas.replace.titleOne': 'An improved version is ready. Replace the old version?',
  'flowIdeas.replace.title': '{{count}} improved versions are ready. Replace the old versions?',
  'flowIdeas.replace.what.links': 'Links in your other flows move to the new version.',
  'flowIdeas.replace.what.keys': 'Saved keys and history move too.',
  'flowIdeas.replace.what.name': 'The new version takes the old name and is switched on.',
  'flowIdeas.replace.what.old': 'The old version is deleted, and you can put it back for 30 days.',
  'flowIdeas.replace.goneOne': '{{old}} is already deleted. Replace finishes the job: links that still point at it move to the new version.',
  'flowIdeas.replace.gone': 'Already deleted: {{names}}. Replace finishes the job: links that still point at them move to the new versions.',
  'flowIdeas.replace.all': 'Replace all',
  'flowIdeas.replace.allTitle': 'Replace {{count}} old versions?',
  'flowIdeas.replace.pair': '{{name}} replaces {{old}}',
  'flowIdeas.replace.allBody': 'Each new version takes the old name and is switched on, and the links in your other flows, saved keys and history move to it. The old versions are deleted; you can put each one back for 30 days.',
  'flowIdeas.replace.one': 'Replace',
  'flowIdeas.replace.oneTitle': 'Replace {{old}}?',
  'flowIdeas.replace.oneBody': '{{name}} takes the name {{old}} and is switched on. Links in your other flows, saved keys and history move to it. The old {{old}} is deleted, and you can put it back for 30 days.',
  'flowIdeas.replace.oneBodyGone': '{{old}} is already deleted. Replace finishes the job: links that still point at it move to {{name}}, which takes the name {{old}} and is switched on.',
  'flowIdeas.replace.oldVersion': 'the old version',
  'flowIdeas.replace.replaced': 'Replaced',
  'flowIdeas.replace.superseded': 'Another version replaced the old one.',
  'flowIdeas.replace.doneOne': 'Replaced. The new version has the old name and is switched on.',
  'flowIdeas.replace.doneAll': 'Old versions replaced: {{count}}. The new versions have the old names and are switched on.',
  'flowIdeas.restore.button': 'Put the old version back',
  'flowIdeas.restore.title': 'Put the old version back?',
  'flowIdeas.restore.body': 'The old version of {{old}} comes back as a separate flow, switched off, so customers notice nothing. Links in your flows stay with the new version.',
  'flowIdeas.restore.done': 'The old version is back, switched off.',
  'flowIdeas.restore.open': 'Open the old version',
  'flowIdeas.error.notImprovement': 'There is no improved version left to replace with.',
  'flowIdeas.error.flowGone': 'The improved version was deleted, so it cannot replace the old one.',
  'flowIdeas.error.nothingToRestore': 'There is no old version to put back.',
  'flowIdeas.error.alreadyRestored': 'The old version is already back.',
  'flowIdeas.error.tooLate': 'Old versions are kept for 30 days only.',
  'flowIdeas.error.planLimitRestore': 'Your plan allows no more flows. Change your plan under Billing, or remove a flow you do not use.'
};

export default flowIdeas;
